import React, { Fragment } from 'react';
import get from 'lodash.get';
import { Cell, Column, HeaderCell } from 'rsuite-table';
import { SortOrder, TextAlignment } from '@philips-internal/oswald-ui-constants';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as ArrowDown } from '../../../../resources/assets/components/DLS_ArrowDown.svg';
import { ReactComponent as ArrowUp } from '../../../../resources/assets/components/DLS_ArrowUp.svg';
import { SuperscriptionItem } from '../../superscription/superscription-item/superscription-item.js';
import { TooltipTrigger } from '../../tooltip/tooltip-constants.js';
import { Tooltip } from '../../tooltip/tooltip.js';
import { DEFAULT_CHECKBOX_COLUMN_WIDTH, DEFAULT_SUPERSCRIPTION_COLUMN_WIDTH } from '../table-column/table-column-constants.js';
import { TableColumn } from '../table-column/table-column.js';
import { getDynamicContent, getFlexAlignment, getSuperscription } from '../table-utils.js';
import { TableCheckbox } from './table-checkbox.js';
const cellAlignmentsClasses = {
    [TextAlignment.CENTER]: 'c-table-cell--align-center',
    [TextAlignment.LEFT]: 'c-table-cell--align-start',
    [TextAlignment.RIGHT]: 'c-table-cell--align-end',
};
function getCheckboxColumn(columnProps) {
    const { alignment, entry, fittedWidth, headerAlignment, id, value } = columnProps;
    const columnWidth = Number(fittedWidth ?? DEFAULT_CHECKBOX_COLUMN_WIDTH);
    return (React.createElement(Column, { key: id, width: columnWidth },
        React.createElement(HeaderCell, { align: headerAlignment, className: "c-table__header-row", width: columnWidth }, MessageService.translate(value)),
        React.createElement(Cell, { className: cellAlignmentsClasses[alignment ?? TableColumn.defaultProps.alignment], dataKey: entry, width: columnWidth }, rowData => {
            const value = get(rowData, entry);
            return (React.createElement(TableCheckbox, { name: entry, value: value }));
        })));
}
function getDefaultColumn(columnProps, onResize, sortedColumn) {
    const { alignment, entry, fittedWidth, formatters = {}, headerAlignment, id, resizable, sortable, sortDirection, value } = columnProps;
    const isSorted = sortedColumn?.name === entry;
    const columnWidth = Number(fittedWidth);
    return (React.createElement(Column, { key: id, onResize: (columnWidth) => (onResize(columnProps, columnWidth)), resizable: resizable, sortable: sortable, width: columnWidth },
        React.createElement(HeaderCell, { align: headerAlignment, className: "c-table__header-row", sortType: sortDirection, width: columnWidth }, isSorted
            ? headerSortCell(MessageService.translate(value), headerAlignment, sortedColumn)
            : MessageService.translate(value)),
        getDefaultCell(entry, formatters, alignment)));
}
function getDefaultCell(entry, formatters, alignment) {
    return (React.createElement(Cell, { className: cellAlignmentsClasses[alignment ?? TableColumn.defaultProps.alignment], dataKey: entry }, (rowData) => {
        const value = get(rowData, entry);
        const cellFormatter = formatters['value'];
        const formattedValue = cellFormatter ? ModelService.format(cellFormatter.name, value, cellFormatter.options) : value;
        return (React.createElement(Tooltip, { multiline: true, triggers: [TooltipTrigger.ELLIPSIS], value: formattedValue },
            React.createElement("div", { className: "u-truncate" }, formattedValue)));
    }));
}
function headerSortCell(value, alignment, sortedColumn) {
    const className = `c-table__header-cell ${getFlexAlignment(alignment)} u-truncate`;
    return (React.createElement("div", { className: className },
        value,
        sortedColumn?.direction === SortOrder.ASC
            ? React.createElement(ArrowUp, { className: "c-table__header-cell__sort-icon" })
            : React.createElement(ArrowDown, { className: "c-table__header-cell__sort-icon" })));
}
function getDynamicColumn(columnProps, onDynamicContentRender) {
    const { alignment, entry, fittedWidth, headerAlignment, id, value } = columnProps;
    const columnWidth = Number(fittedWidth);
    return (React.createElement(Column, { align: alignment, key: id, width: columnWidth },
        React.createElement(HeaderCell, { align: headerAlignment, className: "c-table__header-row", width: columnWidth }, MessageService.translate(value)),
        React.createElement(Cell, { dataKey: entry, width: columnWidth }, (rowData) => {
            const value = get(rowData, entry);
            return (React.createElement(Fragment, null, getDynamicContent(rowData, entry, 'column', value, onDynamicContentRender)));
        })));
}
function getSuperscriptionColumn(columnProps) {
    const { alignment, entry, fittedWidth, headerAlignment, id, showSuperscriptionLabel, superscription, value } = columnProps;
    const className = `${getFlexAlignment(alignment)} u-flex u-truncate`;
    return (React.createElement(Column, { key: id, width: Number(fittedWidth) },
        React.createElement(HeaderCell, { align: headerAlignment, className: "c-table__header-row" }, MessageService.translate(value)),
        React.createElement(Cell, { dataKey: entry }, (rowData, index) => {
            const value = get(rowData, entry);
            const superscriptionItem = getSuperscription(value, superscription);
            if (!superscriptionItem) {
                return null;
            }
            return (React.createElement(SuperscriptionItem, { className: className, key: index, showLabel: showSuperscriptionLabel, ...superscriptionItem }));
        })));
}
function getSuperscriptionBarColumn(columnProps) {
    const { alignment, entry, headerAlignment, id, showSuperscriptionLabel, superscription } = columnProps;
    const className = classNames({
        'c-table-superscription-bar__content': true,
        [getFlexAlignment(alignment)]: true,
        'u-truncate': true,
    });
    return (React.createElement(Column, { key: id, width: DEFAULT_SUPERSCRIPTION_COLUMN_WIDTH },
        React.createElement(HeaderCell, { align: headerAlignment, className: "c-table__header-row" }),
        React.createElement(Cell, { className: "c-table-superscription-bar__cell", dataKey: entry }, (rowData, index) => {
            const value = get(rowData, entry);
            const superscriptionItem = getSuperscription(value, superscription);
            return (React.createElement(Fragment, null, superscriptionItem && (React.createElement(SuperscriptionItem, { className: className, key: index, showLabel: showSuperscriptionLabel, ...superscriptionItem }))));
        })));
}
export { getCheckboxColumn, getDefaultColumn, getDynamicColumn, getSuperscriptionBarColumn, getSuperscriptionColumn, };
//# sourceMappingURL=table-rows.js.map